import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { switchMap } from 'rxjs';
import { NotificationStatus } from '../../core/enums/notification-status.enum';
import { NotificationTranslationType } from '../../core/enums/notification-translation-type.enum';
import { InquiriesRouteType, OrderListRouteType, RouteTypeEnums } from '../../core/enums/route-types.enum';
import { NotificationInterface, NotificationObjectType } from '../../core/models/notification.model';
import { UserService } from '../../core/services/user/user.service';
import { NavbarElements } from '../../core/tree/navbar-elements';
import { TreeService } from '../../core/tree/tree.service';
import { AbstractNotificationComponent } from '../../notification-components/abstract-notification/abstract-notification.component';
import { NotificationsHeaderComponent } from '../../notification-components/notifications-header/notifications-header.component';
import { TimeAgoPipe } from '../../shared/pipes/time-ago/time-ago.pipe';
import { MessageCodeTranslationService } from '../../shared/services/message-code-translation/message-code-translation.service';
import { NotificationService } from '../../shared/services/notification/notification.service';
import { SharedModule } from '../../shared/shared.module';
import { LoaderComponent } from '../../ui-elements/loader/loader.component';
import { ChangePasswordModalComponent } from '../../user/change-password-modal/change-password-modal.component';
import { NotificationOpenerService } from '../opener-services/notification-opener.service';

@Component({
  selector: 'app-notifications-modal',
  templateUrl: './notifications-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SharedModule,
    NotificationsHeaderComponent,
    LoaderComponent,
    InfiniteScrollDirective,
    AbstractNotificationComponent,
    TimeAgoPipe,
  ],
})
export class NotificationsModalComponent implements OnInit {
  private activeModalService = inject(NgbActiveModal);
  private notificationService = inject(NotificationService);
  private userService = inject(UserService);
  private notificationOpenerService = inject(NotificationOpenerService);
  private messageCodeTranslationService = inject(MessageCodeTranslationService);
  private treeService = inject(TreeService);
  private translationService = inject(TranslateService);
  private modalService = inject(NgbModal);

  notifications: NotificationInterface[] = [];
  loading = true;
  currentPage = 1;
  totalPages = 1;
  showInPage: number = 20;

  notificationStatus = NotificationStatus;

  ngOnInit() {
    this.fetchNotifications();
  }

  fetchNotifications() {
    this.loading = true;
    this.notificationService
      .getAll(this.currentPage)
      .noCache()
      .subscribe(({ data, meta }) => {
        this.notifications = [...this.notifications, ...data];
        this.totalPages = meta['total-pages'];
        this.showInPage = meta['show-in-page'];
        this.loading = false;
      });
  }

  onMarkAllAsRead() {
    this.notificationService.markAllAsSeen().subscribe(() => {
      this.ngOnInit();
      this.userService.reload().subscribe(); // Update user notifications
      this.notifications.forEach((notification) => (notification.status = NotificationStatus.SEEN));
    });
  }

  onClose() {
    this.activeModalService.dismiss();
  }

  onOpen(notification: NotificationInterface) {
    this.notificationService
      .markAsSeen(notification.id)
      .pipe(
        switchMap(() => this.userService.reload()), // Update user notifications count
      )
      .subscribe();

    if (this.isExpiringPasswordNotification(notification)) {
      this.modalService.open(ChangePasswordModalComponent, {
        size: 'lg',
        centered: true,
        backdrop: 'static',
        keyboard: false,
      });

      this.onClose();

      return;
    }

    let breadcrumbsRoute: RouteTypeEnums;
    let routeType: OrderListRouteType | InquiriesRouteType;

    switch (notification.object) {
      case NotificationObjectType.CLAIM:
        breadcrumbsRoute = RouteTypeEnums.INQUIRIES;
        routeType = InquiriesRouteType.CLAIMS;
        break;
      case NotificationObjectType.PROJECT_RESERVATION:
        breadcrumbsRoute = RouteTypeEnums.INQUIRIES;
        routeType = InquiriesRouteType.PROJECT_RESERVATIONS;
        break;
      case NotificationObjectType.CUSTOM_MADE_PRICE_REQUEST:
        breadcrumbsRoute = RouteTypeEnums.INQUIRIES;
        routeType = InquiriesRouteType.CUSTOM_MADE_PRICE_REQUESTS;
        break;
      default:
      case NotificationObjectType.ORDER:
        breadcrumbsRoute = RouteTypeEnums.ORDERS;
        routeType = OrderListRouteType.ORDERS;
        break;
    }

    const navbarElement = NavbarElements.find(({ id }) => id === breadcrumbsRoute);
    const secondElement = navbarElement.children.find(({ id }) => id === routeType);
    this.treeService.open(navbarElement, secondElement);

    this.notificationOpenerService.open(notification);
    this.onClose();
  }

  isExpiringPasswordNotification(notification: NotificationInterface): boolean {
    return notification.message === NotificationTranslationType.TRANSLATION_PASSWORD_EXPIRATION;
  }

  isReturnedOrderNotification(notification: NotificationInterface): boolean {
    return notification.message === NotificationTranslationType.TRANSLATION_ORDER_RETURNED;
  }

  getTranslation(notification: NotificationInterface) {
    return this.messageCodeTranslationService.getMessageCodeTranslation(notification);
  }

  allNotificationsSeen(notifications: NotificationInterface[]) {
    return notifications.every((notification) => notification.status === NotificationStatus.SEEN);
  }

  onScrolled() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.fetchNotifications();
    }
  }

  getNotificationTitle(notification: NotificationInterface) {
    const { title, object } = notification;

    if (this.isReturnedOrderNotification(notification)) {
      return this.translationService.instant('NOTIFICATIONS.OBJECT_TITLE.RETURNED_ORDER', { title });
    }

    return this.translationService.instant('NOTIFICATIONS.OBJECT_TITLE.' + object.toUpperCase(), { title });
  }

  getNotificationIcon(notification: NotificationInterface): string {
    if (this.isReturnedOrderNotification(notification)) {
      return 'ni-file-alert';
    }

    if (this.isExpiringPasswordNotification(notification)) {
      return 'ni-lock-outline';
    }

    return 'ni-document';
  }
}
